// A casca: cabeçalho com a loja e o estado do tempo real, menu à esquerda com
// todas as telas, e o miolo da tela ativa. Também é aqui que terminam as
// coisas que chegam de fora do painel: a volta do checkout (BoasVindas) e a
// instalação que começou na Nuvemshop (InstalacaoPendente). O layout é o mesmo do painel legado, pra troca de painel
// não desorientar quem usa o dia inteiro. No modo suporte, uma faixa em cima
// de tudo lembra em que loja se está (FaixaDoSuporte).
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { ehSuporte, useLoja } from '../../shared/estado/loja';
import { sair } from '../../shared/auth/firebase';
import { SeletorDeTema } from '../../shared/tema/SeletorDeTema';
import { useSituacaoDoTempoReal } from '../../shared/tempo-real/tempo-real';
import { Avisos } from '../../shared/ui/avisos';
import { Logo } from '../../shared/ui/Logo';
import { Bia } from '../copiloto/Bia';
import { InstalacaoPendente } from '../integracoes/InstalacaoPendente';
import { BoasVindas } from '../plano/BoasVindas';
import { useEstadoDosAnuncios } from '../estudio/anuncios/api';
import { GRUPOS, telasPara, type Tela } from './telas';
import estilos from './Layout.module.css';

const ROTULO_DA_SITUACAO = { conectando: 'Conectando…', online: 'Painel online', reconectando: 'Reconectando…', parado: 'Desconectado' } as const;

/**
 * Recolher o menu: a faixa cai de 232px para 48px e sobra largura pro miolo.
 *
 * A escolha mora no `localStorage` com a MESMA chave do painel antigo
 * (`nav_collapsed`), pra quem recolheu lá encontrar recolhido aqui. Ler
 * `localStorage` pode estourar (janela anônima, cookies bloqueados), e menu
 * aberto é o padrão razoável quando não dá pra saber.
 */
const CHAVE_DO_MENU = 'nav_collapsed';

function leuRecolhido() {
  try { return localStorage.getItem(CHAVE_DO_MENU) === '1'; } catch { return false; }
}

function ItemDoMenu({ tela, ativa, recolhido }: { tela: Tela; ativa: boolean; recolhido: boolean }) {
  return (
    <Link
      className={`${estilos.item} ${ativa ? estilos.ativo : ''}`}
      to={tela.rota}
      aria-current={ativa ? 'page' : undefined}
      // Recolhido só sobra o ícone: sem o title, o menu vira adivinhação.
      title={recolhido ? tela.rotulo : undefined}
    >
      {/* O ícone tem elemento próprio pra poder ter CAIXA. Solto num nó de
          texto, o tamanho dele seria o font-size do item — e emoji desenha
          maior que o nominal, então não haveria como dizer "20x20" e ser
          verdade. */}
      <span className={estilos.icone} aria-hidden="true">{tela.icone}</span>
      <span className={estilos.rotulo}>{tela.rotulo}</span>
    </Link>
  );
}

/**
 * A faixa do modo suporte: o super admin numa loja de cliente. Fica em cima de
 * tudo e na cor de aviso porque o perigo é esquecer onde se está — o painel é
 * idêntico ao da própria loja dele, e uma mudança feita "na minha" cairia na
 * do cliente.
 *
 * "Voltar" vai pra loja em que ele é dono; sem nenhuma, pra primeira da lista.
 * A loja aberta não está em `lojas` (o servidor manda a lista DELE), então
 * qualquer uma dali é de fato uma volta.
 */
function FaixaDoSuporte() {
  const { nomeDaLoja, lojas, trocar } = useLoja();
  const minha = lojas.find((l) => l.papel === 'dono') ?? lojas[0] ?? null;
  return (
    <div className={estilos.suporte} role="status">
      <span className={estilos.suporteTexto}>🛟 <strong>Modo suporte</strong> — você está vendo a loja <strong>{nomeDaLoja || 'sem nome'}</strong>. Tudo o que você faz aqui fica registrado.</span>
      <span className={estilos.suporteAcoes}>
        <Link to="/app/super" className={estilos.suporteBotao}>Trocar de loja</Link>
        {minha && <button type="button" className={estilos.suporteBotao} onClick={() => trocar(minha.id)}>Voltar para a minha loja</button>}
      </span>
    </div>
  );
}

/**
 * Quem rola é a JANELA (2026-09-28), e não o miolo: com a rolagem dentro de um
 * `<main>` de altura fixa, extensões de captura de página inteira (GoFullPage,
 * o "tamanho real" do DevTools) só pegavam a primeira tela, e no celular a
 * barra de endereço nunca recolhia. O cabeçalho e o menu ficam presos
 * (`sticky`), e as telas precisam saber a altura do que está preso em cima:
 *
 *   --altura-do-topo   o cabeçalho (e a faixa do suporte, quando há) — onde o
 *                      menu lateral encosta;
 *   --topo-da-tela     isso mais o menu, quando ele é a faixa horizontal do
 *                      celular — onde o que é preso DENTRO de uma tela encosta,
 *                      e o que a tela de Mensagens desconta para caber inteira.
 *
 * Medido, e não escrito à mão: a faixa do suporte quebra linha em tela
 * estreita, e um número fixo esconderia o começo do conteúdo embaixo dela.
 */
function useAlturasPresas() {
  const topo = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLElement>(null);
  const [alturas, setAlturas] = useState({ topo: 0, faixa: 0 });
  useLayoutEffect(() => {
    const medir = () => {
      const t = topo.current?.offsetHeight ?? 0;
      const n = menu.current;
      // Faixa horizontal = o menu está em cima do conteúdo, e não ao lado.
      const faixa = n && getComputedStyle(n.parentElement as Element).flexDirection === 'column' ? n.offsetHeight : 0;
      setAlturas((antes) => (antes.topo === t && antes.faixa === faixa ? antes : { topo: t, faixa }));
    };
    medir();
    // A janela mudando de largura é o que troca o menu de lateral para faixa.
    // Só o observador não bastou: medido no navegador, ele não disparou nessa
    // troca, e a tela de Mensagens ficou 74px mais baixa até recarregar.
    window.addEventListener('resize', medir);
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);   // jsdom não tem
    if (topo.current) observador?.observe(topo.current);
    if (menu.current) observador?.observe(menu.current);
    return () => { window.removeEventListener('resize', medir); observador?.disconnect(); };
  }, []);
  const estilo = { '--altura-do-topo': `${alturas.topo}px`, '--topo-da-tela': `${alturas.topo + alturas.faixa}px` } as CSSProperties;
  return { topo, menu, estilo };
}

export function Layout() {
  const { papel, lojas, tenantId, trocar, superAdmin } = useLoja();
  const situacao = useSituacaoDoTempoReal();
  const { pathname } = useLocation();
  const [recolhido, setRecolhido] = useState(leuRecolhido);
  // Anúncios entra aos poucos: o item só aparece quando o servidor diz que a loja foi liberada.
  const anuncios = useEstadoDosAnuncios(papel === 'dono' || papel === 'admin' || papel === 'suporte');
  const telas = telasPara(papel, superAdmin, new Set(anuncios.data?.liberado ? ['anuncios' as const] : []));
  const suporte = ehSuporte(papel);
  const grupos = Array.from(new Set(telas.map((t) => t.grupo)));

  const { topo, menu, estilo } = useAlturasPresas();

  // A janela rola, então a posição da tela anterior viria junto na troca: a
  // tela nova abriria no meio. Cada rota começa do topo.
  useEffect(() => {
    if (/jsdom/.test(navigator.userAgent)) return;   // o jsdom não implementa scrollTo
    window.scrollTo({ top: 0 });
  }, [pathname]);

  const alternarMenu = useCallback(() => setRecolhido((antes) => !antes), []);
  // Guardar é efeito colateral: falhar ao gravar não pode impedir o menu de
  // recolher agora, só de lembrar na próxima visita.
  useEffect(() => {
    try { localStorage.setItem(CHAVE_DO_MENU, recolhido ? '1' : '0'); } catch { /* segue sem lembrar */ }
  }, [recolhido]);

  return (
    <div className={estilos.app} style={estilo}>
      <BoasVindas />
      <InstalacaoPendente />
      {/* Preso no topo, junto: a faixa do suporte não pode sumir rolando — ela
          é o que impede confundir a loja do cliente com a própria. */}
      <div ref={topo} className={estilos.topo}>
        {suporte && <FaixaDoSuporte />}
        <header className={estilos.cabecalho}>
          <Link to="/app" className={estilos.logo} aria-label="borainbox — início"><Logo /></Link>
          <div className={estilos.acoes}>
            <span className={estilos.situacao} data-situacao={situacao}>
              <span className={estilos.pontinho} aria-hidden="true" />{ROTULO_DA_SITUACAO[situacao]}
            </span>
            {/* No suporte a loja aberta não está na lista (é a dele, não a do
                cliente): o seletor mostraria outra loja como se fosse a atual. A
                faixa de cima faz a troca. */}
            {!suporte && lojas.length > 1 && (
              <label className={estilos.troca}>
                <span className="fraco">Loja</span>
                <select value={tenantId ?? ''} onChange={(e) => trocar(e.target.value)} aria-label="Trocar de loja">
                  {lojas.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.papel}</option>)}
                </select>
              </label>
            )}
            <Bia />
            <SeletorDeTema />
            {/* Só o ícone, como o seletor de tema ao lado; o nome continua no
                leitor de tela e no title. */}
            <button type="button" className={estilos.sair} aria-label="Sair" title="Sair" onClick={() => void sair().then(() => { location.href = '/app/entrar'; })}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
            </button>
          </div>
        </header>
      </div>
      <div className={estilos.corpo}>
        <nav ref={menu} className={`${estilos.menu} ${recolhido ? estilos.menuRecolhido : ''}`} aria-label="Telas do painel">
          <button
            type="button"
            className={estilos.recolher}
            onClick={alternarMenu}
            aria-expanded={!recolhido}
            aria-label={recolhido ? 'Expandir menu' : 'Recolher menu'}
            title={recolhido ? 'Expandir menu' : 'Recolher menu'}
          >{recolhido ? '\u00bb' : '\u00ab'}</button>
          {grupos.map((g) => (
            <div key={g} className={estilos.grupo}>
              <div className={estilos.tituloDoGrupo}>{GRUPOS[g]}</div>
              {telas.filter((t) => t.grupo === g).map((t) => <ItemDoMenu key={t.chave} tela={t} ativa={!!t.rota && pathname === t.rota} recolhido={recolhido} />)}
            </div>
          ))}
        </nav>
        <main className={estilos.miolo}><Outlet /></main>
      </div>
      <Avisos />
    </div>
  );
}
